import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose, setDisplayName } from 'recompose';
import { find, compact, isEmpty } from 'lodash';
import FontAwesome from 'react-fontawesome';
import { Trans, withI18n } from 'lingui-react';
import { withTheme } from 'styled-components';
import { theme as defaultTheme } from 'decisiv-ui-utils';
import { ButtonLink, QuickActionButton } from 'base-components';

import cardType from 'utils/cardType';
import PdfLink from 'components/PdfLink';

import Dialog from 'elements/Dialog';
import Divider from 'elements/Divider';
import DropDown from 'elements/DropDown';
import {
  SplitBlock,
  SplitBlockPart,
  SplitBlockElement,
} from 'elements/SplitBlock';
import Widget from 'elements/Widget';

import { formatDate } from 'utils/timeUtils';
import { getOutputText } from 'utils/widget';
import LabelValue from 'components/LabelValue';

import DropdownWrapper from './DropdownWrapper';
import Wrapper from './Wrapper';
import NoteDialog from './NoteDialog';
import messages from './messages';

import withConnectedData from './Container';

export function getApprovalStatus(approveValue) {
  switch (approveValue) {
    case 'Approve':
      return 'approved';
    case 'Decline':
      return 'declined';
    default:
      return '';
  }
}

export function getOkLabel(title, i18n) {
  switch (title) {
    case 'Approve':
      return i18n._(messages.accept);
    case 'Decline':
      return i18n._(messages.decline);
    default:
      return title;
  }
}

export class EstimateInvoicePendingWidget extends Component {
  static propTypes = {
    caseInfo: PropTypes.shape({
      approvalStatus: PropTypes.string,
      estimateTotal: PropTypes.string,
      invoiceId: PropTypes.string,
      invoiceTotal: PropTypes.string,
      roNumber: PropTypes.string,
      poNumber: PropTypes.string,
    }).isRequired,
    casesError: PropTypes.shape(),
    configName: PropTypes.string.isRequired,
    i18n: PropTypes.shape({
      _: PropTypes.func,
    }).isRequired,
    theme: PropTypes.shape({
      colors: PropTypes.shape({
        base: PropTypes.shape({
          chrome100: PropTypes.string.isRequired,
          chrome200: PropTypes.string.isRequired,
          chrome600: PropTypes.string.isRequired,
        }).isRequired,
        brand: PropTypes.shape({
          primary: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    updateCase: PropTypes.func.isRequired,
    addHiddenWidget: PropTypes.func.isRequired,
  };

  static defaultProps = {
    cardType: '',
    caseInfo: {},
    casesError: {},
    casesRequesting: false,
    theme: defaultTheme,
  };

  state = {
    selectedApproveValue: undefined,
    dialogVisible: false,
    note: '',
    poNumber: null,
  };

  componentWillMount() {
    if (!this.widgetShouldRender()) {
      const { configName, addHiddenWidget } = this.props;
      addHiddenWidget(configName);
    }
  }

  onChangeApproveOption = ({ value }) => {
    this.deferredApproveValue = value;
    this.setState({
      dialogVisible: true,
    });
  }

  onChangePONumber = (ev) => {
    this.setState({
      poNumber: ev.target.value,
    });
  }

  getApproveOptions = () => {
    const { i18n } = this.props;
    return [
      {
        value: 'Approve',
        label: (
          <ButtonLink>
            {i18n._(messages.approve)}
          </ButtonLink>
        ),
      },
      {
        value: 'Decline',
        label: (
          <ButtonLink>
            {i18n._(messages.decline)}
          </ButtonLink>
        ),
      },
    ];
  }

  getDropdownColors = (selectedApproveValue) => {
    const { theme } = this.props;
    if (selectedApproveValue === 'Approve') {
      return {
        fgColor: theme.colors.base.chrome100,
        bgColor: theme.colors.brand.primary,
      };
    } else if (selectedApproveValue === 'Decline') {
      return {
        fgColor: theme.colors.base.chrome100,
        bgColor: theme.colors.base.chrome600,
      };
    }

    return {
      fgColor: undefined,
      bgColor: undefined,
    };
  }

  handleOk = (note) => {
    this.setState({
      selectedApproveValue: this.deferredApproveValue,
      dialogVisible: false,
      note,
    });
  }

  handleCancel = () => {
    this.setState({
      dialogVisible: false,
    });
  }

  handleSubmit = () => {
    const { caseInfo, updateCase } = this.props;
    const { poNumber, note, selectedApproveValue } = this.state;

    if (!selectedApproveValue) {
      return;
    }
    const approvalStatus = getApprovalStatus(this.deferredApproveValue);

    updateCase({
      caseId: caseInfo.id,
      versionId: caseInfo.version,
      approvalStatus,
      comments: note,
      poNumber: poNumber === null ? caseInfo.poNumber : poNumber,
    });
  }

  widgetShouldRender() {
    const { approvalStatus } = this.props.caseInfo;
    return approvalStatus === 'Pending';
  }

  render() {
    const {
      casesError,
      caseInfo,
      i18n,
      theme,
    } = this.props;
    const {
      selectedApproveValue,
      dialogVisible,
      note,
    } = this.state;

    const {
      approvalStatus,
      etr,
      roUrl,
      roNumber,
      estimateTotal,
      poNumber,
    } = caseInfo;

    const formattedEtr = etr ? formatDate(etr) : getOutputText(etr);
    const approveOptions = this.getApproveOptions();
    const selectedApproveOption = find(
      approveOptions, { value: selectedApproveValue },
    );
    const dropdownColors = this.getDropdownColors(selectedApproveValue);
    const submitDisabled = !selectedApproveValue && 'disabled';
    const statusCardType = cardType(approvalStatus);

    if (!this.widgetShouldRender()) {
      return null;
    }

    return (
      <Wrapper>
        <Widget
          id="estimate-invoice"
          className="estimate-invoice-pending"
          expandKey="estimate-invoice"
        >
          <Widget.Header>
            <Trans id={messages.title} />
          </Widget.Header>

          <SplitBlock modifiers={['pad']}>
            <SplitBlock modifiers={['pad']}>
              <Widget.SubHeader modifiers={compact([statusCardType])}>
                {approvalStatus}
              </Widget.SubHeader>
            </SplitBlock>

            <SplitBlockPart modifiers={['right']}>
              <LabelValue
                label={<Trans id={messages.etr} />}
                value={formattedEtr}
                marginRight={80}
              />
              <LabelValue
                label={<Trans id={messages.repairOrder} />}
                value={
                  <span>
                    {roNumber && '#'}
                    {getOutputText(roNumber)}
                  </span>
                }
              />
            </SplitBlockPart>
          </SplitBlock>

          <Divider color={theme.colors.base.chrome200} marginBottom={15} />

          <SplitBlock modifiers={['pad']}>
            <SplitBlock modifiers={['pad']}>
              <div className="estimate-label">
                <Trans id={messages.estimateTotal} />
              </div>
              <div className="estimate-value">
                {estimateTotal}
              </div>
            </SplitBlock>
            <DropdownWrapper className="pending-estimate-options">
              <DropDown
                onChange={this.onChangeApproveOption}
                options={approveOptions}
                value={selectedApproveOption}
                widgetBGColor={dropdownColors.bgColor}
                widgetFGColor={dropdownColors.fgColor}
                showIcon={!!note}
              />
              {dialogVisible &&
              <Dialog>
                <NoteDialog
                  title={this.deferredApproveValue}
                  note={note}
                  theme={theme}
                  okLabel={getOkLabel(this.deferredApproveValue, i18n)}
                  onOk={this.handleOk}
                  onCancel={this.handleCancel}
                />
              </Dialog>
              }
            </DropdownWrapper>
          </SplitBlock>

          <Widget.Item>
            <SplitBlock modifiers={['pad']}>
              <LabelValue
                label={<Trans id={messages.po} />}
                value={<input
                  placeholder="Optional: PO Number"
                  style={{ width: '150px', height: '25px', paddingLeft: '10px' }}
                  defaultValue={poNumber}
                  onChange={this.onChangePONumber}
                />}
              />
            </SplitBlock>
          </Widget.Item>

          <Divider color={theme.colors.base.chrome200} marginTop={15} />

          <Widget.Item>
            <SplitBlock modifiers={['pad']}>
              <SplitBlockPart modifiers={['left']}>
                <PdfLink url={roUrl}>
                  <Trans id={messages.viewEstimate} />
                </PdfLink>
              </SplitBlockPart>
              <SplitBlockPart modifiers={['right']}>
                <SplitBlockElement modifiers={['padLeft']}>
                  {!isEmpty(casesError) &&
                    <span className="error">
                      <span className="bold">
                        <FontAwesome name="ban" />
                        <Trans id={messages.error} />:&nbsp;
                      </span>
                    </span>
                  }
                </SplitBlockElement>
                <QuickActionButton
                  modifiers={compact([
                    submitDisabled,
                    'secondary',
                    'hoverSuccess',
                  ])}
                  onClick={this.handleSubmit}
                >
                  <QuickActionButton.Text>
                    <Trans id={messages.submit} />
                  </QuickActionButton.Text>
                </QuickActionButton>
              </SplitBlockPart>
            </SplitBlock>
          </Widget.Item>
        </Widget>
      </Wrapper>
    );
  }
}


export default compose(
  setDisplayName('EstimateInvoicePendingWidget'),
  withConnectedData,
  withTheme,
  withI18n(),
)(EstimateInvoicePendingWidget);



// WEBPACK FOOTER //
// ./compositions/EstimateInvoicePendingWidget/index.js